/**
 * 密码登录步骤
 * 用户名 + 密码 登录
 */
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, User, Loader2, Eye, EyeOff } from 'lucide-react';
import authService from '../../../../services/authService';

const PasswordLoginStep = ({ onSuccess, onError, onForgotPassword }) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!username || !password) {
            setError('请填写完整信息');
            return;
        }

        setSubmitting(true);
        setError('');

        try {
            const response = await authService.login({
                username,
                password,
            });
            onSuccess?.(response);
        } catch (err) {
            const errorMsg = err.response?.data?.error || '登录失败，请检查账号密码';
            setError(errorMsg);
            onError?.(errorMsg);
        } finally {
            setSubmitting(false);
        }
    };

    const inputClass = "w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm transition-all focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 focus:bg-white focus:outline-none";

    return (
        <div className="pt-2">
            {/* 标题 */}
            <div className="text-center mb-5">
                <div className="w-11 h-11 mx-auto mb-2 bg-gradient-to-br from-gray-600 to-gray-800 rounded-xl flex items-center justify-center shadow-md shadow-gray-500/20">
                    <Lock className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">账号密码登录</h3>
                <p className="text-xs text-gray-400 mt-0.5">使用用户名和密码登录</p>
            </div>

            {/* 错误提示 */}
            {error && (
                <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-2.5 mb-3 bg-red-50 border border-red-100 rounded-lg text-xs text-red-600"
                >
                    {error}
                </motion.div>
            )}

            <form onSubmit={handleSubmit} className="space-y-2.5">
                {/* 用户名 */}
                <div className="relative">
                    <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">
                        <User className="w-4 h-4" />
                    </div>
                    <input
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="请输入用户名 / 手机号"
                        className={inputClass}
                    />
                </div>

                {/* 密码 */}
                <div className="relative">
                    <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">
                        <Lock className="w-4 h-4" />
                    </div>
                    <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="请输入密码"
                        className="w-full pl-11 pr-11 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm transition-all focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 focus:bg-white focus:outline-none"
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                </div>

                {typeof onForgotPassword === 'function' && (
                    <div className="text-right">
                        <button
                            type="button"
                            onClick={onForgotPassword}
                            className="text-xs text-blue-500 hover:text-blue-600 font-medium transition-colors"
                        >
                            忘记密码?
                        </button>
                    </div>
                )}

                {/* 登录按钮 */}
                <motion.button
                    type="submit"
                    disabled={submitting || !username || !password}
                    whileHover={{ scale: submitting ? 1 : 1.01 }}
                    whileTap={{ scale: submitting ? 1 : 0.99 }}
                    className={`w-full py-3 rounded-xl font-semibold text-sm text-white transition-all ${submitting || !username || !password
                        ? 'bg-gray-300 cursor-not-allowed'
                        : 'bg-gradient-to-r from-gray-700 to-gray-900 shadow-md shadow-gray-500/20'
                        }`}
                >
                    {submitting ? (
                        <div className="flex items-center justify-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>登录中...</span>
                        </div>
                    ) : (
                        '登录'
                    )}
                </motion.button>
            </form>

            {/* 协议提示 */}
            <p className="text-center text-[10px] text-gray-400 mt-3">
                登录即表示同意
                <button className="text-blue-500 hover:underline mx-0.5">服务协议</button>
                和
                <button className="text-blue-500 hover:underline mx-0.5">隐私政策</button>
            </p>
        </div>
    );
};

export default PasswordLoginStep;
